import { useCallback, useEffect, useMemo, useState } from "react";
import {
  DashboardChartCatalogItem,
  DashboardChartStageConfig,
  fetchDashboardChartConfigs,
} from "@/modules/administration/features/StochasticDashboardChartConfig/api/fetchDashboardChartConfigs";
import { updateDashboardChartConfig } from "@/modules/administration/features/StochasticDashboardChartConfig/api/updateDashboardChartConfig";

export function useDashboardChartConfigPage() {
  const [catalog, setCatalog] = useState<DashboardChartCatalogItem[]>([]);
  const [stages, setStages] = useState<DashboardChartStageConfig[]>([]);
  const [selectedStageUuid, setSelectedStageUuid] = useState<string>("");
  const [workingKeys, setWorkingKeys] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetchDashboardChartConfigs();
      setCatalog(response.data.catalog);
      setStages(response.data.stages);
      return response.data.stages;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load().then((loadedStages) => {
      if (loadedStages && loadedStages.length > 0) {
        setSelectedStageUuid(loadedStages[0].uuid);
        setWorkingKeys(loadedStages[0].chartKeys);
      }
    });
  }, [load]);

  const selectedStage = useMemo(
    () => stages.find((stage) => stage.uuid === selectedStageUuid) ?? null,
    [stages, selectedStageUuid],
  );

  const selectStage = (uuid: string) => {
    setSelectedStageUuid(uuid);
    const stage = stages.find((s) => s.uuid === uuid);
    setWorkingKeys(stage ? stage.chartKeys : []);
  };

  const isDirty = useMemo(
    () =>
      selectedStage !== null &&
      JSON.stringify(workingKeys) !== JSON.stringify(selectedStage.chartKeys),
    [selectedStage, workingKeys],
  );

  const includedKeys = workingKeys;
  const availableCharts = useMemo(
    () => catalog.filter((item) => !workingKeys.includes(item.key)),
    [catalog, workingKeys],
  );

  const labelFor = useCallback(
    (key: string) => catalog.find((item) => item.key === key)?.label ?? key,
    [catalog],
  );

  const addChart = (key: string) => setWorkingKeys((keys) => [...keys, key]);

  const removeChart = (key: string) =>
    setWorkingKeys((keys) => keys.filter((k) => k !== key));

  const moveChart = (key: string, direction: -1 | 1) =>
    setWorkingKeys((keys) => {
      const index = keys.indexOf(key);
      const target = index + direction;
      if (index === -1 || target < 0 || target >= keys.length) return keys;
      const next = [...keys];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });

  const persist = async (chartKeys: string[]) => {
    if (!selectedStage) return;
    setSaving(true);
    try {
      await updateDashboardChartConfig(selectedStage.uuid, { chartKeys });
      const loadedStages = await load();
      const refreshed = loadedStages?.find(
        (stage) => stage.uuid === selectedStage.uuid,
      );
      if (refreshed) setWorkingKeys(refreshed.chartKeys);
    } finally {
      setSaving(false);
    }
  };

  const save = () => persist(workingKeys);

  // Saving an empty list clears the stage's rows — it falls back to the
  // code-defined defaults, which the reload then reflects.
  const resetToDefaults = () => persist([]);

  return {
    catalog,
    stages,
    selectedStage,
    selectStage,
    includedKeys,
    availableCharts,
    labelFor,
    addChart,
    removeChart,
    moveChart,
    save,
    resetToDefaults,
    isDirty,
    loading,
    saving,
  };
}
